"use client";

import clsx from "clsx";
import { animate, useMotionValue, useReducedMotion, type ValueAnimationTransition } from "motion/react";
import * as m from "motion/react-m";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from "react";

import { DeckCard } from "./DeckCard";
import { wrapIndex, wrapRank } from "./fanMath";
import type { DeckItem } from "./types";

// Horizontal drag distance (px) that advances the deck by one card.
const DRAG_DISTANCE_PER_CARD = 240;
// Pointer movement (px) below which a press counts as a tap rather than a drag.
const TAP_SLOP_PX = 5;
const SNAP_SPRING = { damping: 24, mass: 0.7, stiffness: 170 } as const;
// Intro master clock: linear — every card eases inside its own stagger window (see fanMath), so
// the deck flows open card after card in one continuous cascade.
const INTRO_TRANSITION = { delay: 0.15, duration: 1.1, ease: "linear" } as const;
// Riffle (tap on a back card): seconds for the first step plus a little more per extra card, so
// the deck visibly slides through every video on the way to the tapped one.
const RIFFLE_BASE_S = 0.4;
const RIFFLE_PER_CARD_S = 0.28;

export type HeroVideoDeckProps = {
  items: DeckItem[];
  titleEnd?: string;
  titleStart?: string;
};

function srcKey(src: DeckItem["imageSrc"]) {
  return typeof src === "string" ? src : src.src;
}

export default function HeroVideoDeck({ items, titleEnd = "", titleStart = "" }: HeroVideoDeckProps) {
  if (items.length === 0) {
    return null;
  }

  // Remount when the deck contents change — not just its length — so position, activeIndex and the
  // motion values never stay aligned to a previous, different deck (e.g. a same-length swap).
  const deckKey = JSON.stringify(items.map(item => item.id ?? srcKey(item.imageSrc)));

  return <FanDeck items={items} key={deckKey} titleEnd={titleEnd} titleStart={titleStart} />;
}

function FanDeck({ items, titleEnd, titleStart }: { items: DeckItem[]; titleEnd: string; titleStart: string }) {
  const total = items.length;
  const fullTitle = [titleStart, titleEnd].filter(Boolean).join(" ");
  const position = useMotionValue(0);
  const intro = useMotionValue(0);
  const reducedMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  // Sound is enabled deck-wide (not per card) so it stays on as cards auto-advance. It lives here
  // because each card's <video> unmounts when the deck moves; resetting it on the FanDeck remount
  // (deck contents change) starts a fresh deck muted again.
  const [soundOn, setSoundOn] = useState(false);
  const dragRef = useRef<{
    moved: boolean;
    pointerId: number;
    pressedIndex: number | null;
    startPosition: number;
    startX: number;
  } | null>(null);
  const snapRef = useRef<ReturnType<typeof animate> | null>(null);
  // True while a snap/riffle is easing `position`; gates auto-advance so a clip that ends mid-flight
  // can't fight the in-flight animation. Cleared on completion and at every stop site.
  const isAnimatingRef = useRef(false);
  // Card index reported by the DeckCard a press lands on; consumed by the next pointer-down.
  const pressedIndexRef = useRef<number | null>(null);

  useEffect(() => {
    const unsubscribe = position.on("change", value => {
      const next = wrapIndex(Math.round(value), total);
      setActiveIndex(current => (current === next ? current : next));
    });

    return unsubscribe;
  }, [position, total]);

  // Play the stacked→fan intro on mount (instant for reduced-motion users).
  useEffect(() => {
    if (reducedMotion) {
      intro.set(1);

      return;
    }

    const controls = animate(intro, 1, INTRO_TRANSITION);

    return () => controls.stop();
  }, [reducedMotion, intro]);

  // Stop any in-flight snap/riffle and clear the in-flight guard. Centralised so every stop site
  // resets the guard — otherwise an interrupted, held riffle would leave it stuck true and
  // permanently disable auto-advance.
  function stopAnimation() {
    snapRef.current?.stop();
    isAnimatingRef.current = false;
  }

  function animateTo(target: number, transition: ValueAnimationTransition<number> = SNAP_SPRING) {
    stopAnimation();

    if (reducedMotion) {
      position.set(target);

      return;
    }

    isAnimatingRef.current = true;
    snapRef.current = animate(position, target, {
      ...transition,
      onComplete: () => {
        isAnimatingRef.current = false;
      },
    });
  }

  // Settle onto the card `delta` slots ahead of the nearest one (0 = snap back to the nearest).
  function step(delta: number) {
    animateTo(Math.round(position.get()) + delta);
  }

  // Cards report which of them a press lands on (their pointer-down bubbles ahead of the deck's).
  function handleCardPress(index: number) {
    pressedIndexRef.current = index;
  }

  function enableSound() {
    setSoundOn(true);
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.pointerType === "mouse" && event.button !== 0) {
      // Discard any card press a non-primary button registered, so a later primary press on empty
      // deck chrome can't riffle to that stale index.
      pressedIndexRef.current = null;

      return;
    }

    stopAnimation();
    dragRef.current = {
      moved: false,
      pointerId: event.pointerId,
      // Consume the card press so a later press on empty deck space can't reuse a stale index.
      pressedIndex: pressedIndexRef.current,
      startPosition: position.get(),
      startX: event.clientX,
    };
    pressedIndexRef.current = null;
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;

    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }

    const delta = event.clientX - drag.startX;

    // Only a real drag (past the tap slop) counts as dragging — flipping the flag on plain
    // presses would unmount the active video and restart its clip on every tap.
    if (!drag.moved && Math.abs(delta) > TAP_SLOP_PX) {
      drag.moved = true;
      setIsDragging(true);
    }

    position.set(drag.startPosition - delta / DRAG_DISTANCE_PER_CARD);
  }

  function handlePointerEnd(event: ReactPointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;

    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }

    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setIsDragging(false);

    if (!drag.moved && handleTap(drag.pressedIndex)) {
      return;
    }

    step(0);
  }

  // A press without movement: if it landed on a back card, riffle forward through the deck —
  // sliding through every card in between so their videos surface one after another — until the
  // tapped card reaches the front.
  function handleTap(pressedIndex: number | null) {
    if (pressedIndex === null) {
      return false;
    }

    const current = Math.round(position.get());
    const steps = wrapRank(pressedIndex - current, total);

    if (steps < 1) {
      return false;
    }

    animateTo(current + steps, { duration: RIFFLE_BASE_S + RIFFLE_PER_CARD_S * (steps - 1), ease: "easeInOut" });

    return true;
  }

  // Auto-advance to the next card once the active clip finishes playing — but only when the deck is
  // genuinely at rest on a card. While a snap/riffle is in flight `isAnimatingRef` is true (the
  // spring can pass within 0.01 of an intermediate integer mid-animation), and an interrupted, held
  // riffle leaves `position` between cards; ignoring `ended` in both cases keeps a transient clip
  // from aborting the animation onto the wrong card. A settled front card sits on an integer with no
  // animation running, so the clip that genuinely ends still advances.
  function handleVideoEnded() {
    if (isAnimatingRef.current) {
      return;
    }

    if (Math.abs(position.get() - Math.round(position.get())) > 0.01) {
      return;
    }

    step(1);
  }

  function handleKeyDown(event: ReactKeyboardEvent) {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      step(-1);
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      step(1);
    }
  }

  return (
    <section className='tw-relative tw-w-full tw-overflow-x-clip tw-bg-paper'>
      <div className='tw-mx-auto tw-flex tw-w-full tw-max-w-[75rem] tw-flex-col tw-items-center tw-gap-10 tw-px-6 tw-py-16 lg:tw-flex-row lg:tw-justify-center lg:tw-gap-6 lg:tw-py-24'>
        {/* Semantic heading for assistive tech + SEO */}
        {fullTitle && <h1 className='tw-sr-only'>{fullTitle}</h1>}

        {/* Mobile heading (full, single block) */}
        {fullTitle && (
          <p aria-hidden className='tw-text-center tw-type-4xl tw-text-black lg:tw-hidden'>
            {fullTitle}
          </p>
        )}

        {/* Desktop heading start */}
        {titleStart && (
          <p aria-hidden className='tw-hidden tw-whitespace-nowrap tw-type-6xl tw-text-black lg:tw-block'>
            {titleStart}
          </p>
        )}

        {/* Fanned deck — drag to scrub through cards */}
        <m.div
          aria-label={fullTitle || undefined}
          className={clsx(
            "tw-relative tw-flex tw-w-[26rem] tw-max-w-full tw-shrink-0 tw-touch-none tw-items-center tw-justify-end tw-rounded-[24px] tw-outline-none focus-visible:tw-outline-focus lg:tw-w-[28rem]",
            isDragging ? "tw-cursor-grabbing" : "tw-cursor-grab",
          )}
          onKeyDown={handleKeyDown}
          onPointerCancel={handlePointerEnd}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          role='group'
          tabIndex={0}
        >
          <div className='tw-relative tw-aspect-[184/238] tw-w-44 tw-shrink-0 lg:tw-w-48'>
            {items.map((item, index) => (
              <DeckCard
                item={item}
                index={index}
                intro={intro}
                isActive={!isDragging && activeIndex === index}
                key={item.id ?? index}
                onEnableSound={enableSound}
                onPress={handleCardPress}
                onVideoEnded={handleVideoEnded}
                position={position}
                soundOn={soundOn}
                total={total}
              />
            ))}
          </div>
        </m.div>

        {/* Desktop heading end */}
        {titleEnd && (
          <p aria-hidden className='tw-hidden tw-whitespace-nowrap tw-type-6xl tw-text-black lg:tw-block'>
            {titleEnd}
          </p>
        )}
      </div>
    </section>
  );
}
